import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertCircle, CalendarPlus, Clock, Check } from 'lucide-react';
import { formatPhoneBR, normalizePhone } from '../lib/phone';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();

  const initialTab = searchParams.get('tab') === 'novo-paciente' ? 'register' : 'login';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Appointment scheduling
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [wantsAppointment, setWantsAppointment] = useState(false);

  const fetchAvailability = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/public/availability`);
      setAvailableDates(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      // If input has any letter or '@', treat as email (send trimmed/lowercase).
      // Otherwise treat as phone — strip all non-digits before sending.
      const raw = loginIdentifier.trim();
      const isEmailLike = /[a-zA-Z@]/.test(raw);
      const identifier = isEmailLike ? raw.toLowerCase() : normalizePhone(raw);
      if (!identifier) {
        setError('Informe e-mail ou celular');
        setLoading(false);
        return;
      }
      const result = await login(identifier, loginPassword);
      if (result.success) {
        navigate(result.role === 'doctor' ? '/doctor' : '/patient');
      } else { setError(result.error || 'Erro ao fazer login'); }
    } catch (err) { setError('Erro ao fazer login'); }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await register({
        name: registerName,
        phone: normalizePhone(registerPhone),
        password: registerPassword,
        role: 'patient'
      });
      if (result.success) {
        // If patient selected an appointment, book it
        if (wantsAppointment && selectedDate && selectedTime) {
          try {
            await axios.post(`${API_URL}/api/patient/appointments/book`, {
              date: selectedDate, time: selectedTime, type: 'clinica_geral', notes: 'Primeira consulta (agendada no cadastro)'
            }, { withCredentials: true });
          } catch (bookErr) { console.error('Auto-book error:', bookErr); }
        }
        navigate('/patient');
      } else { setError(result.error || 'Erro ao criar conta'); }
    } catch (err) { setError('Erro ao criar conta'); }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    const callbackUrl = `${window.location.origin}/api/auth/google/callback`;
    const popup = window.open(`https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(callbackUrl)}&response_type=code&scope=email%20profile&access_type=offline`, '_blank', 'width=500,height=600');
    if (!popup) { window.location.href = callbackUrl; }
  };

  const slotsForDate = availableDates.find(d => d.date === selectedDate)?.slots || [];

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
    return `${weekdays[d.getDay()]}, ${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex relative overflow-hidden bg-[#0F1B3D]">
        <img
          src="/images/dra-fernanda-photo.png"
          alt="Dra. Fernanda Garcia Passos"
          className="w-full h-full object-cover object-top scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1B3D] via-[#0F1B3D]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <h1 className="text-3xl lg:text-4xl font-medium text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Garcia Passos</h1>
          <p className="text-lg text-white/80">Clinica Medica</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8 bg-[#F5F0E8] overflow-y-auto max-h-screen">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 md:hidden">
            <img
              src="/images/logo-gp.png"
              alt="Garcia Passos Clinica Medica"
              className="h-14 mx-auto mb-4"
            />
            <h2 className="text-3xl font-medium text-[#0F1B3D]">Bem-vindo</h2>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Novo Paciente</TabsTrigger>
            </TabsList>

            {/* LOGIN TAB */}
            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Entrar</CardTitle>
                  <CardDescription>Entre com sua conta para continuar</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    {error && activeTab === 'login' && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="error-message">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="login-identifier">Email ou Celular</Label>
                      <Input id="login-identifier" type="text" placeholder="seu@email.com ou (00) 00000-0000" value={loginIdentifier} onChange={(e) => {
                        const v = e.target.value;
                        // If the input contains any letter or '@', treat as email and DON'T mask.
                        // Only apply phone mask when input has digits/phone separators only.
                        const hasLetterOrAt = /[a-zA-Z@]/.test(v);
                        setLoginIdentifier(hasLetterOrAt ? v : formatPhoneBR(v));
                      }} required data-testid="login-identifier-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Senha</Label>
                      <Input id="login-password" type="password" placeholder="********" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required data-testid="login-password-input" />
                    </div>
                    <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" disabled={loading} data-testid="login-submit-button">
                      {loading ? 'Entrando...' : 'Entrar'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* REGISTER TAB */}
            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Novo Paciente</CardTitle>
                  <CardDescription>Crie sua conta e agende sua primeira consulta</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    {error && activeTab === 'register' && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="error-message">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nome Completo *</Label>
                      <Input id="register-name" type="text" placeholder="Seu nome" value={registerName} onChange={(e) => setRegisterName(e.target.value)} required data-testid="register-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-phone">Celular (WhatsApp) *</Label>
                      <Input id="register-phone" type="tel" inputMode="numeric" maxLength={15} placeholder="(00) 00000-0000" value={registerPhone} onChange={(e) => setRegisterPhone(formatPhoneBR(e.target.value))} required data-testid="register-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Senha *</Label>
                      <Input id="register-password" type="password" placeholder="********" value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} required data-testid="register-password-input" />
                    </div>

                    {/* Optional Appointment Scheduling */}
                    <div className="border border-[#E5DCC8] rounded-lg p-4 space-y-3 bg-[#FAF6EE]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CalendarPlus className="h-4 w-4 text-[#0F1B3D]" />
                          <span className="text-sm font-medium text-[#0F1B3D]">Agendar primeira consulta</span>
                        </div>
                        <button type="button" onClick={() => { setWantsAppointment(!wantsAppointment); setSelectedDate(''); setSelectedTime(''); }}
                          className={`relative w-10 h-5 rounded-full transition-colors ${wantsAppointment ? 'bg-[#0F1B3D]' : 'bg-[#D1D5DB]'}`}
                          data-testid="toggle-appointment">
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${wantsAppointment ? 'translate-x-5' : ''}`} />
                        </button>
                      </div>
                      <p className="text-xs text-[#7A85A0]">Opcional - Voce tambem pode agendar depois pelo painel</p>

                      {wantsAppointment && (
                        <div className="space-y-3 pt-2">
                          {availableDates.length === 0 ? (
                            <p className="text-sm text-[#7A85A0] text-center py-2">Nenhum horario disponivel no momento</p>
                          ) : (
                            <>
                              <div className="space-y-2">
                                <Label className="text-xs">Escolha uma data</Label>
                                <div className="flex flex-wrap gap-2">
                                  {availableDates.slice(0, 8).map(av => (
                                    <button key={av.date} type="button"
                                      onClick={() => { setSelectedDate(av.date); setSelectedTime(''); }}
                                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${selectedDate === av.date ? 'bg-[#0F1B3D] text-white border-[#0F1B3D]' : 'border-[#E5DCC8] text-[#5F6B85] hover:border-[#0F1B3D]'}`}
                                      data-testid={`date-${av.date}`}>
                                      {formatDate(av.date)}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {selectedDate && slotsForDate.length > 0 && (
                                <div className="space-y-2">
                                  <Label className="text-xs">Escolha um horario</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {slotsForDate.map(slot => (
                                      <button key={slot} type="button"
                                        onClick={() => setSelectedTime(slot)}
                                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all flex items-center gap-1 ${selectedTime === slot ? 'bg-[#0F1B3D] text-white border-[#0F1B3D]' : 'border-[#E5DCC8] text-[#5F6B85] hover:border-[#0F1B3D]'}`}
                                        data-testid={`time-${slot}`}>
                                        <Clock className="h-3 w-3" />{slot}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {selectedDate && selectedTime && (
                                <div className="flex items-center gap-2 p-2 bg-[#0F1B3D]/10 rounded-lg">
                                  <Check className="h-4 w-4 text-[#0F1B3D]" />
                                  <span className="text-xs text-[#0F1B3D] font-medium">
                                    Consulta em {formatDate(selectedDate)} as {selectedTime}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" disabled={loading} data-testid="register-submit-button">
                      {loading ? 'Criando...' : wantsAppointment && selectedDate && selectedTime ? 'Criar Conta e Agendar' : 'Criar Conta'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
